import { useState } from 'react';
import { NavLink } from 'react-router';

import { Avatar, Drawer, MonoLabel, focusRingOnDark } from '@/design-system';
import { primaryRole } from '@/entities/user';
import { accountCopy } from '@/features/account/copy';
import { authCopy } from '@/features/auth/copy';
import { tenantSwitchError, useTenantSwitch } from '@/features/auth/hooks';
import { TenantBadge } from '@/features/auth/TenantBadge';
import { TenantPicker } from '@/features/auth/TenantPicker';
import { ROLE_LABELS, useSessionStore } from '@/shared/api';
import { cn } from '@/shared/lib';

import { paths } from '../router/paths';

/**
 * Wie ben je, en waar ben je?
 *
 * Onderaan de rail, onder een rule: initialen, je naam, en daaronder je rol mét
 * de naam van het bedrijf. Die bedrijfsnaam staat er altijd bij en niet in een
 * menu: iemand die voor drie keuringsbedrijven werkt moet nooit hoeven raden in
 * wiens omgeving hij een keuring opvoert.
 *
 * Het blok opent je **Account** (apparaten en wachtwoord). Heb je meerdere
 * lidmaatschappen, dan staat er rechts een eigen knop die de omgevingskeuze
 * opent; met één lidmaatschap valt er niets te kiezen.
 */
export function RailUserBlock() {
    const session = useSessionStore((state) => state.session);
    const switchTenant = useTenantSwitch();
    const [open, setOpen] = useState(false);

    if (!session) {
        return null;
    }

    const { user, tenant, memberships } = session;
    const canSwitch = memberships.length > 1;
    const role = primaryRole(session.roles);
    /* De sessie draagt de omgeving: ook na een koude start zonder netwerk. */
    const meta = [role ? ROLE_LABELS[role] : null, tenant?.name].filter(Boolean).join(' · ');

    const identity = (
        <>
            <Avatar size={28} variant="initials" onDark>
                {initialsFor(user.name)}
            </Avatar>

            <span className="flex min-w-0 flex-col">
                <span className="truncate text-[13px] leading-[1.3] text-[var(--rail-name)]">
                    {user.name}
                </span>
                <span className="truncate text-[11px] leading-[1.3] text-[var(--rail-muted)]">
                    {meta}
                </span>
            </span>
        </>
    );

    return (
        <>
            <div className="mt-[6px] flex items-stretch gap-1 border-t border-[var(--rail-border)] pt-[6px]">
                <NavLink
                    to={paths.account}
                    data-tour="account"
                    aria-label={accountCopy.railLink(user.name)}
                    className={({ isActive }) =>
                        cn(
                            'flex min-w-0 flex-1 items-center gap-[10px] rounded-[var(--radius-control)] p-[10px] no-underline',
                            isActive
                                ? 'bg-[var(--rail-item-active)]'
                                : 'hover:bg-[var(--rail-item-hover)]',
                            focusRingOnDark,
                        )
                    }
                >
                    {identity}
                </NavLink>

                {canSwitch ? (
                    <button
                        type="button"
                        aria-haspopup="dialog"
                        aria-expanded={open}
                        aria-label={authCopy.tenantSwitchTitle}
                        onClick={() => setOpen(true)}
                        className={cn(
                            'shrink-0 cursor-pointer rounded-[var(--radius-control)] px-2',
                            'hover:bg-[var(--rail-item-hover)]',
                            focusRingOnDark,
                        )}
                    >
                        <MonoLabel size={9} tone="onDarkMuted">
                            {authCopy.tenantSwitchHint}
                        </MonoLabel>
                    </button>
                ) : null}
            </div>

            <Drawer
                open={open}
                onClose={() => {
                    switchTenant.reset();
                    setOpen(false);
                }}
                position="fixed"
                side="left"
                width={318}
                title={authCopy.tenantSwitchTitle}
                className="gap-[14px] p-[18px]"
            >
                {tenant ? (
                    <div className="flex flex-col gap-2">
                        <MonoLabel size={10}>{authCopy.tenantCurrent}</MonoLabel>
                        <TenantBadge tenant={tenant} roles={session.roles} />
                    </div>
                ) : null}

                <TenantPicker
                    memberships={memberships}
                    currentTenantUuid={tenant?.uuid ?? null}
                    busyTenantUuid={
                        switchTenant.isPending
                            ? (switchTenant.variables?.tenant.uuid ?? null)
                            : null
                    }
                    title={authCopy.tenantSwitchTitle}
                    description={authCopy.tenantSwitchBody}
                    error={switchTenant.isError ? tenantSwitchError(switchTenant.error) : null}
                    onPick={(membership) => {
                        if (membership.tenant.uuid === tenant?.uuid) {
                            setOpen(false);

                            return;
                        }

                        switchTenant.mutate(
                            { tenant: membership.tenant },
                            { onSuccess: () => setOpen(false) },
                        );
                    }}
                />
            </Drawer>
        </>
    );
}

function initialsFor(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);

    if (parts.length === 0) {
        return '?';
    }

    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';

    return (first + last).toUpperCase();
}
